import {
  schemaClass,
  schemaElementEnumTypes,
  schemaElementOptional,
  schemaElementPropType,
  schemaProp,
} from '@zipari/json-decorators';
import { getValue } from '@zipari/web-utils';

// We might not even want to name these and just let them decide from the component how to handle this
// But it would be good to have a standard sort set of things that can be passed in.
export enum ZipRuleAction {
  SHOW = 'SHOW',
  DISABLE = 'DISABLE',
  THEME = 'THEME',
  HIDE = 'HIDE',
  COPY = 'COPY',
  ASSIGN = 'ASSIGN',
  RESET = 'RESET',
  READONLY = 'READONLY',
  INVALIDATE_FORM = 'INVALIDATE_FORM',
  ELEMENT_MESSAGE = 'ELEMENT_MESSAGE',
  ROUTE = 'ROUTE',
}

// Maybe we want to do something even cooler than this but this will work for now;
export enum ZipRuleComparator {
  CHANGES_TO = 'CHANGES_TO',
  CURRENTDATECOMPARE = 'CURRENTDATECOMPARE',
  COMPAREYEAR = 'COMPAREYEAR',
  EXISTS = 'EXISTS',
  NOT_EXISTS = 'NOT_EXISTS',
  INCLUDES = 'INCLUDES',
  GREATER_THAN = 'GREATER_THAN',
  VIEWED = 'VIEWED',
  LESS_THAN = 'LESS_THAN',
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
}

export enum Connectives {
  OR = 'OR',
  AND = 'AND',
}

export const allowedZipRuleActions = {
  ELEMENT_MESSAGE: 'ELEMENT_MESSAGE',
  SHOW: 'SHOW',
  DISABLE: 'DISABLE',
  THEME: 'THEME',
  HIDE: 'HIDE',
  COPY: 'COPY',
  ASSIGN: 'ASSIGN',
  RESET: 'RESET',
  READONLY: 'READONLY',
  SET_KEY: 'SET_KEY',
  INVALIDATE_FORM: 'INVALIDATE_FORM',
  ROUTE: 'ROUTE',
};

export class ZipRulesConfig {
  // Map of callbacks to be called if it passes relationship tests
  actionMap!: { [key: string]: Function };
}

@schemaClass()
export class WhenRelation {
  @schemaElementOptional()
  @schemaProp({ description: 'prop to look for the when' })
  prop?: string;

  @schemaElementOptional()
  @schemaProp({ description: 'Which comparator to use' })
  comparator?: string;

  @schemaElementOptional()
  @schemaProp({ description: 'What value to compare against' })
  value?: any;

  @schemaElementOptional()
  @schemaProp({
    description: 'Any extra values that need to be used for the rule',
  })
  extra?: any;

  // Recursive to allow for nested when relations
  @schemaElementEnumTypes(Connectives)
  @schemaElementOptional()
  @schemaProp({ description: 'Ability to connect multiple whenRelations' })
  connective?: Connectives;

  // todo figure out how to add schema prop with recursion
  when?: Array<WhenRelation>;
  from: unknown;
}

export class Relation {
  @schemaElementEnumTypes(ZipRuleAction)
  @schemaProp({ description: 'Action to complete' })
  action: ZipRuleAction = ZipRuleAction.SHOW;

  @schemaElementOptional()
  @schemaElementEnumTypes(Connectives)
  @schemaProp({ description: 'Ability to connect multiple whenRelations' })
  connective?: Connectives;

  @schemaElementPropType(WhenRelation)
  @schemaProp({ description: 'Ability to connect multiple whenRelations' })
  when: Array<WhenRelation> = [];

  @schemaProp({ description: 'Arguments to be returned to the actions' })
  returnArgs: Array<any> = [];
}

export class ZipRules {
  context: any;
  prevContext: any;

  constructor(context, prevContext?) {
    this.context = context;
    this.prevContext = prevContext;
  }

  public getRuleResult(whens: Array<WhenRelation>, connective: string) {
    let _connectiveValue = false;

    for (const when of whens) {
      if (when.when) {
        return this.getRuleResult(when.when, when.connective);
      } else {
        const resultOfCompare = this.executeIndividualZipRule(
          this.context,
          when,
        );

        if (connective === 'OR') {
          if (resultOfCompare) {
            _connectiveValue = true;
            break;
          }
        } else {
          if (!resultOfCompare) {
            _connectiveValue = false;
            break;
          }
          _connectiveValue = true;
        }
      }
    }

    return _connectiveValue;
  }

  public executeIndividualZipRule(
    data: Record<string, unknown>,
    individual_whenRel: WhenRelation,
  ) {
    // TODO Add in the rest of the comparators
    switch (individual_whenRel.comparator) {
      case ZipRuleComparator.CHANGES_TO:
        let CHANGES_TO_RESULT = null;
        const currValue = getValue(this.context || {}, individual_whenRel.prop);
        const previousValue = getValue(
          this.prevContext || {},
          individual_whenRel.prop,
        );

        // here we know that the two values are different
        if (currValue !== previousValue) {
          // check to see if we need to check what the value came from
          let fromCheck = true;
          if (individual_whenRel.from) {
            fromCheck = previousValue === individual_whenRel['from'];
          }

          // if this also is true then we know that the value has just changed to this value
          if (currValue === individual_whenRel.value && fromCheck) {
            CHANGES_TO_RESULT = true;
          }
        }

        return CHANGES_TO_RESULT;
      case ZipRuleComparator.CURRENTDATECOMPARE: // all possible format options start with the same letter, eg: DAYS, DAY, d, D, etc.
        // So lowercase the format and just use the first letter
        const formats = { d: 'days', m: 'month', y: 'year' };
        type FormatKeys = keyof typeof formats;
        const formatStrings = Object.values(formats);
        type FormatOptions = (typeof formatStrings)[number];

        const milliseconds_to_formats: Record<FormatOptions, number> = {
          days: 86400000,
          month: 2592000000,
          year: 31556952000000,
        };

        // determine the milliseconds since epoch time for each date
        const valFromData = getValue(data, individual_whenRel.prop);
        const dateToCompare = new Date(valFromData).getTime();
        const currentDate = new Date().getTime();

        // determine the name of the format to use for the math
        const formatKey: FormatKeys = individual_whenRel.extra.format
          .slice(0)
          .toLowerCase();
        const format: FormatOptions = formats[formatKey];
        if (!format) {
          console.warn('CURRENTDATECOMPARE format not set up correctly');

          return null;
        }

        // incorporate any padding involved in the compare
        const currentDateIncludingPadding =
          currentDate +
          (individual_whenRel.extra.currDatePadding || 0) *
            milliseconds_to_formats[format];
        const dateToCompareIncludingPadding =
          dateToCompare +
          (individual_whenRel.extra.propDatePadding || 0) *
            milliseconds_to_formats[format];

        // determine the actual amount of the specified firnat between the two dates
        const result =
          (currentDateIncludingPadding - dateToCompareIncludingPadding) /
          milliseconds_to_formats[format];

        // send back whether or not the user wanted to know if it was greater or less than the current date
        // we'll know this by whether or not its a positive number
        if (individual_whenRel.value === 'GREATER') {
          return result > 0;
        } else if (individual_whenRel.value === 'LESS') {
          return result < 0;
        } else {
          console.warn('CURRENTDATECOMPARE comparison not set up correctly');
        }
        break;
      // NOTE: The final logic for viewed file is to be decided.
      // Either we can check all files or just the first item. Most of time
      // there is only one file available.
      // eslint-disable-next-line no-fallthrough
      case ZipRuleComparator.VIEWED:
        if (getValue(data, individual_whenRel.prop)) {
          return (
            !!getValue(data, individual_whenRel.prop)[0].viewed ===
            individual_whenRel.value
          );
        } else {
          return false;
        }
      case ZipRuleComparator.COMPAREYEAR:
        const itemToFind = individual_whenRel.prop?.split('.')[0];

        if (!itemToFind || !data[itemToFind]) {
          return null;
        }

        const currentValue = getValue(data, individual_whenRel.prop);
        // make sure that the value exists first
        // if it does then do the comparison otherwise just send up true because we may not need it to be
        // required right now
        if (currentValue) {
          const currentYear = parseInt(currentValue.substring(0, 4));
          // check the value given to know what we should do for the compare function...
          // only options right now are less than or greater than
          if (currentYear > 1850 && individual_whenRel.value[0] === '>') {
            return (
              new Date().getFullYear() - currentYear >
              parseInt(
                individual_whenRel.value.substring(
                  1,
                  individual_whenRel.value.length,
                ),
              )
            );
          } else if (
            currentYear > 1850 &&
            individual_whenRel.value[0] === '<'
          ) {
            return (
              new Date().getFullYear() - currentYear <
              parseInt(
                individual_whenRel.value.substring(
                  1,
                  individual_whenRel.value.length,
                ),
              )
            );
          } else {
            return null;
          }
        } else {
          return null;
        }
      case ZipRuleComparator.INCLUDES:
        const val = getValue(data, individual_whenRel.prop);
        return val.includes(individual_whenRel.value);
      case ZipRuleComparator.NOT_EXISTS:
        const _value2 = getValue(data, individual_whenRel.prop);
        if (Array.isArray(_value2)) {
          const checkArr = _value2.filter((item) => {
            return item === undefined || item === null || item === '';
          });

          return checkArr.length === _value2.length;
        } else {
          return _value2 === undefined || _value2 === null || _value2 === '';
        }
      case ZipRuleComparator.EXISTS:
        const _value = getValue(data, individual_whenRel.prop);
        if (Array.isArray(_value)) {
          const checkArr = _value.filter((item) => {
            return !(item === undefined || item === null || item === '');
          });

          return checkArr.length > 0;
        } else {
          return !(_value === undefined || _value === null || _value === '');
        }
      case ZipRuleComparator.EQUALS:
        return (
          getValue(data, individual_whenRel.prop) === individual_whenRel.value
        );
      case ZipRuleComparator.NOT_EQUALS:
        return (
          getValue(data, individual_whenRel.prop) !== individual_whenRel.value
        );
      default:
        console.warn('comparator not supported');
        return null;
    }
  }
}
